
/**
 * Enhanced valuation form hook
 * Created: 2025-04-23
 * This hook properly manages form state to avoid React queue errors
 * Updated: 2025-04-29 - Fixed state handling to ensure dialog displays with results
 * Updated: 2025-05-25 - Fixed onSubmit handler to use proper type signature
 * Updated: 2025-05-26 - Added better continue handling and enhanced with useValuationContinue
 * Updated: 2025-10-26 - Changed validation mode to onBlur to prevent error messages from blocking user input
 * Updated: 2025-11-09 - Added submission lock and token lifecycle management
 * Updated: 2026-01-20 - Suppressed error toast when manual valuation is required (show only dialog)
 * Updated: 2026-03-07 - Added campaign tracking events (valuation_started, valuation_completed)
 */

import { useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ValuationFormData, valuationFormSchema } from "@/types/validation";
import { useValuationErrorDialog } from "./useValuationErrorDialog";
import { getValuation, cleanupValuationData } from "@/components/hero/valuation/services/valuationService";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { SubmitHandler } from "react-hook-form";
import { useValuationContinue } from "./useValuationContinue";
import { useValuationStore } from "@/hooks/store/useValuationStore";
import { TurnstileWidgetRef } from "@/components/security/TurnstileWidget";
import { useTrackEvent } from "@/hooks/useTrackEvent";

export const useEnhancedValuationForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [valuationResult, setValuationResult] = useState<any>(null);
  const isSubmitting = useRef(false); // Submission lock
  const turnstileWidgetRef = useRef<TurnstileWidgetRef>(null);
  const trackEvent = useTrackEvent();
  
  // Get Turnstile token and actions from store
  const { turnstileToken, setTurnstileToken } = useValuationStore();
  
  // Use the valuation continue hook for navigation handling
  const { handleContinue: navigateToListing } = useValuationContinue();
  
  // Properly initialize dialog state
  const { 
    isOpen: errorDialogOpen, 
    setIsOpen: setErrorDialogOpen 
  } = useValuationErrorDialog();

  // Setup form with validation
  const form = useForm<ValuationFormData>({
    resolver: zodResolver(valuationFormSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      vin: "",
      mileage: "",
      gearbox: "manual",
    },
  });

  // Form submission handler with proper typing for React Hook Form
  const handleFormSubmit: SubmitHandler<ValuationFormData> = useCallback(async (data: ValuationFormData) => {
    // Prevent duplicate submissions
    if (isSubmitting.current) {
      console.log('[Form] Submission already in progress, ignoring duplicate');
      return;
    }
    
    console.log("Form submission with data:", data);
    
    // Track valuation started
    trackEvent({ event_type: "valuation_started", metadata: { vin: data.vin } });
    // Lock submission
    isSubmitting.current = true;
    setIsLoading(true);
    
    // Capture current token before clearing
    const tokenToUse = turnstileToken;
    
    try {
      const mileage = parseInt(data.mileage) || 0;
      
      // Store in localStorage for possible retrieval later
      localStorage.setItem("tempMileage", data.mileage);
      localStorage.setItem("tempVIN", data.vin);
      localStorage.setItem("tempGearbox", data.gearbox);
      
      // Clear token immediately to prevent reuse
      console.log('[Form] Clearing Turnstile token before API call');
      setTurnstileToken(null);
      
      const result = await getValuation(
        data.vin,
        mileage,
        data.gearbox,
        tokenToUse
      );
      
      console.log("Valuation result:", result);
      
      // Mark token as consumed - reset widget
      if (turnstileWidgetRef.current) {
        console.log('[Form] Resetting Turnstile widget after submission');
        setTimeout(() => {
          turnstileWidgetRef.current?.reset();
        }, 500);
      }
      
      if (result.success && result.data) {
        // Track valuation completed
        trackEvent({ event_type: "valuation_completed", metadata: { vin: data.vin, estimated_price: result.data.reservePrice } });
        
        // Store the full result
        setValuationResult(result.data);
        localStorage.setItem("valuationData", JSON.stringify(result.data));
        
        // Ensure we show the dialog AFTER setting the result
        setTimeout(() => {
          setShowDialog(true);
          console.log("Dialog should be visible now");
        }, 100);
      } else {
        // Dialog handles all error display — no toast needed
        const resultAny = result as any;
        
        setValuationResult({
          error: result.error,
          vin: data.vin,
          transmission: data.gearbox,
          mileage: parseInt(data.mileage) || 0,
          requiresManualValuation: resultAny?.requiresManualValuation,
          code: resultAny?.code
        });
        
        // Show dialog (either error dialog or manual valuation prompt)
        setTimeout(() => {
          setShowDialog(true);
          console.log("Dialog should be visible now");
        }, 100);
      }
    } catch (error: any) {
      console.error("Valuation error:", error);
      
      // Reset widget on error
      if (turnstileWidgetRef.current) {
        setTimeout(() => {
          turnstileWidgetRef.current?.reset();
        }, 500);
      }
      
      // Dialog handles all error display — no toast needed
      const errorMessage = error.message || "Wystąpił błąd";
      
      setValuationResult({
        error: errorMessage,
        vin: data.vin,
        transmission: data.gearbox,
        mileage: parseInt(data.mileage) || 0
      });
      
      // Show error dialog
      setTimeout(() => {
        setShowDialog(true);
        console.log("Exception dialog should be visible now");
      }, 100);
    } finally {
      setIsLoading(false);
      // Unlock submission
      isSubmitting.current = false;
    }
  }, [turnstileToken, setTurnstileToken]);

  // Handler for continuing to car listing
  const handleContinue = useCallback(() => {
    console.log("Continue to listing triggered", {
      valuationData: valuationResult ? {
        make: valuationResult.make,
        model: valuationResult.model,
        vin: valuationResult.vin,
        reservePrice: valuationResult.reservePrice
      } : null,
      timestamp: new Date().toISOString()
    });
    
    try {
      // Close the dialog
      setShowDialog(false);
      
      // Use the valuation continue hook to handle navigation
      navigateToListing(valuationResult);
      
      toast.success("Przygotowywanie ogłoszenia", {
        description: "Konfigurowanie danych samochodu"
      });
    } catch (error) {
      console.error("Error in continue handler:", error);
      toast.error("Błąd nawigacji", {
        description: "Spróbuj ponownie lub odśwież stronę"
      });
      
      // As a fallback, try direct navigation
      try {
        navigate('/sell-my-car?from=valuation');
      } catch (navError) {
        console.error("Direct navigation also failed:", navError);
      }
    }
  }, [navigate, valuationResult, navigateToListing]);

  // Reset form state
  const resetForm = useCallback(() => {
    form.reset();
    setValuationResult(null);
    setShowDialog(false);
    cleanupValuationData();
    console.log("Form reset complete");
  }, [form]);

  return {
    form,
    isLoading,
    showDialog,
    setShowDialog,
    errorDialogOpen,
    setErrorDialogOpen,
    valuationResult,
    handleFormSubmit, // Use the properly typed handler now
    handleContinue,
    resetForm,
    turnstileWidgetRef // Expose ref for connecting to TurnstileWidget
  };
};
